import React, { useCallback, useEffect, useRef } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';

import Layout from '../../shared/components/Layout/Layout';
import Header from './components/Header';
import JournalEntryList from './components/JournalEntryList';
import ModalHandler from './ModalHandler';
import SubModalHandler from './SubModalHandler';

import iconSet from '../../shared/images/teambuildr-selection.json';
import { ColorGreen, ColorWhite } from '../../shared/GlobalStyles';

import lightenDarkenColor from '../../shared/utils/lightenDarkenColor';
import { journalTracker } from '../../shared/utils/amplitudeHelper';
import {
  getGroups,
  getJournalEntriesForAllAthletes,
  getJournalEntriesForAthlete,
  getJournalEntriesForGroup,
  getMeJournalEntries,
  getUsers,
  setActiveReduxModal,
  setExerciseTab,
  setSelectedAthlete,
} from './ducks/journalActions';

const CreateButtonWrapper = styled('button')`
  align-items: center;  
  background-color: ${ColorGreen};
  border: none;
  border-radius: 100%;
  bottom: 15px;
  cursor: pointer;
  display: flex;
  height: 56px;
  justify-content: center;
  position: fixed;
  right: 25px;
  transition: all 250ms ease-in-out;
  width: 56px;
  z-index: 1000;
  &:hover {
    background-color: ${lightenDarkenColor(ColorGreen, -25)};
  }
  @media screen and (max-width: 768px) {
    right: 15px;
  }
`;

const JournalLayout = styled('div')`
  margin-left: auto;
  margin-right: auto;
  max-width: 600px;
  scroll-margin-top: 50px;
`;

const StyledLayout = styled(Layout)`
  @media only screen and (max-width: 540px) {
    .container {
      padding-left: 0;
      padding-right: 0;
    }
    .css-12eirj4-MainContent {
      padding-left: 0;
      padding-right: 0;
    }
  }
`;

const Journal = () => {
  const {
    activeTab,
    currentAthlete,
    currentGroup,
    dateRange,
    filterExercise,
    mediaOnly,
  } = useSelector((state) => state.journal.data);
  const { currentUser } = useSelector((state) => state.auth.data);
  const {
    accountCode, admin, id: currentUserId, pic,
  } = currentUser;
  const coachAccount = admin > 0;
  const dispatch = useDispatch();
  const topRef = useRef(null);

  const scrollToTop = useCallback(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const handleCreateEntryClick = useCallback(() => {
    dispatch(setActiveReduxModal('create-entry-modal'));
    journalTracker('Main page', 'Create entry modal opened');
  }, [dispatch]);

  useEffect(() => {
    if (accountCode && currentUserId) {
      batch(() => {
        if (admin > 0) {
          dispatch(getUsers(accountCode));
          dispatch(getGroups(accountCode));
        }
        dispatch(
          setSelectedAthlete({
            first: 'My',
            id: currentUserId,
            last: 'Journal',
            pic: pic || null,
          }),
        );
        dispatch(setExerciseTab('L', accountCode));
      });
    }
  }, [accountCode, admin, currentUserId, dispatch, pic]);

  // refresh journal content as tab or filter options are changed
  useEffect(() => {
    if (!accountCode || !currentUserId) return;

    if (!coachAccount || currentAthlete?.id === currentUserId) {
      dispatch(getMeJournalEntries(accountCode));
    } else if (activeTab === 'SINGLE_ATHLETE') {
      dispatch(getJournalEntriesForAthlete(accountCode));
    } else if (activeTab === 'GROUPS') {
      dispatch(getJournalEntriesForGroup(accountCode));
    } else if (activeTab === 'ALL_ATHLETES') {
      dispatch(getJournalEntriesForAllAthletes(accountCode));
    }

    if (topRef?.current) {
      scrollToTop();
    }
  }, [
    accountCode,
    activeTab,
    coachAccount,
    currentAthlete?.id,
    currentGroup?.id,
    currentUserId,
    dateRange,
    dispatch,
    filterExercise,
    mediaOnly,
    scrollToTop,
    topRef.current,
  ]);

  return (
    <StyledLayout>
      <Helmet>
        <title>Journal | TeamBuildr</title>
      </Helmet>
      <JournalLayout ref={topRef}>
        <Header scrollToTop={scrollToTop} />
        <JournalEntryList />
        {activeTab === 'SINGLE_ATHLETE' && (
          <CreateButtonWrapper aria-label='Create new journal entry' onClick={handleCreateEntryClick}>
            <IcomoonReact iconSet={iconSet} size={20} icon='plus' color={ColorWhite} />
          </CreateButtonWrapper>
        )}
        <ModalHandler scrollToTop={scrollToTop} />
        <SubModalHandler />
      </JournalLayout>
    </StyledLayout>
  );
};

export default Journal;
